import type { CurriculumTermPlanDto } from './api/academic.api';

// ─── Qué campos sobreviven al guardar un término de la malla ─────────────────
//
// LA REGLA VIVE ACÁ PORQUE SE ESCRIBÍA CUATRO VECES, y a la cuarta falló.
//
// QUÉ PASÓ (2026-09-28, producción). Se añadieron `year` y `semester` al
// término para que la escalera pudiera colocar por año. Se agregaron al DTO, al
// editor, al esquema de entrada del servidor y al mapeo de escritura —los
// cuatro sitios que uno encuentra buscando—. Faltó el quinto: la pantalla del
// programa rearmaba cada término campo por campo antes de enviarlo, así que los
// dos campos NUEVOS se caían EN EL NAVEGADOR, antes de salir.
//
// El efecto fue el peor posible: la pantalla decía que había guardado, el
// director escribía el año, recargaba y no estaba. Y peor todavía, ese guardado
// BORRÓ los años que ya tenía el programa.
//
// El test que había probaba el servidor, y el dato nunca llegaba al servidor.
//
// POR ESO ESTO ES UNA SOLA FUNCIÓN: el día que el término gane otro campo, hay
// un único lugar donde recordarlo, y un test que lo vigila.

/** Los campos opcionales del término, en un solo lugar. Añadir uno acá es todo
 *  lo que hay que hacer del lado del navegador. */
const OPCIONALES = ['label', 'electiveSlots', 'year', 'semester'] as const;

/**
 * Lo que llega de un formulario: los dos campos que un término no puede no
 * tener, y los opcionales que pueden venir a medio escribir — es decir,
 * explícitamente `undefined`.
 *
 * EL TIPO DE ENTRADA ADMITE `undefined` Y EL DE SALIDA NO. Ésa es justamente la
 * frontera que esta función existe para cruzar, y escribirla en el tipo evita
 * el `as` que la anulaba: con la conversión forzada, un `{ termOffset:
 * undefined }` pasaba el compilador y dejaba un término sin su posición.
 */
export interface TerminoEnEdicion {
  termOffset: number;
  recommendedCourseIds: string[];
  label?: string | undefined;
  electiveSlots?: number | undefined;
  year?: number | undefined;
  semester?: number | undefined;
}

/**
 * El término listo para enviar: sin las claves opcionales que quedaron vacías
 * —`exactOptionalPropertyTypes` no admite `undefined` explícito— y con la
 * etiqueta recortada.
 *
 * NO INVENTA VALORES. Un año ausente se queda ausente: la escalera distingue
 * «no declarado» de un año concreto, y rellenarlo movería una malla que nadie
 * pidió mover.
 */
export function terminoParaGuardar(tm: TerminoEnEdicion): CurriculumTermPlanDto {
  const salida: CurriculumTermPlanDto = {
    termOffset: tm.termOffset,
    recommendedCourseIds: tm.recommendedCourseIds,
  };
  for (const campo of OPCIONALES) {
    const valor = tm[campo];
    if (valor === undefined) continue;
    if (campo === 'label') {
      const texto = String(valor).trim();
      if (texto) salida.label = texto;
      continue;
    }
    // A partir de acá el campo es numérico; el `continue` de arriba dejó fuera
    // el único que no lo es.
    if (typeof valor !== 'number' || !Number.isFinite(valor)) continue;
    // LOS VALORES IMPOSIBLES NO VIAJAN. Un año 0 o negativo no existe en
    // ninguna escalera y, guardado, se colaría como una columna real delante de
    // todas las demás; un semestre que no sea 1 ni 2 no es una mitad de año. El
    // servidor también los rechaza, pero devolvería un error opaco donde basta
    // con que el campo a medio escribir todavía no se guarde.
    if (campo === 'year' && valor < 1) continue;
    if (campo === 'semester' && valor !== 1 && valor !== 2) continue;
    if (campo === 'electiveSlots' && valor < 0) continue;
    (salida[campo] as number) = valor;
  }
  return salida;
}
